import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { Link } from 'react-router-dom';

const getJobDate = (job) => job?.posted_on || job?.posted_date || job?.created_at;
const getJobStatus = (job) =>
  job?.status || (job?.is_active === false ? 'Closed' : 'Active');

function Dashboard() {
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    activeJobs: 0,
    premiumMembers: 0,
    revenue: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    userGrowth: 0,
    jobPostings: 0,
    subscriptions: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await adminAPI.getDashboardOverview({
        activity_page_size: 10,
        jobs_page: 1,
        jobs_page_size: 10,
      });

      const summary = data?.summary || {};
      setDashboardData({
        totalUsers: summary.total_users || 0,
        activeJobs: summary.active_jobs || 0,
        premiumMembers: summary.premium_members || 0,
        revenue: Number(summary.revenue || 0),
      });
      setPerformanceMetrics({
        userGrowth: summary.user_growth || 0,
        jobPostings: summary.job_postings_growth || 0,
        subscriptions: summary.subscription_rate || 0,
      });
      setRecentActivity(data?.recent_activity?.results || []);
      setRecentJobs(data?.recent_jobs?.results || []);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to format relative time
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return 'recently';
    
    const now = new Date();
    const date = new Date(timestamp);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'yesterday';
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  // Helper function to get icon for activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'registration':
        return <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path></svg>;
      case 'job':
        return <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd"></path></svg>;
      case 'payment':
        return <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"></path><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"></path></svg>;
      case 'jobseeker':
        return <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"></path></svg>;
      default:
        return <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path></svg>;
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800';
      case 'closed':
        return 'bg-rose-100 text-rose-800';
      case 'draft':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  // Helper function to get appropriate background color for activity icons
  const getActivityIconBackground = (type) => {
    switch (type) {
      case 'registration':
        return 'bg-blue-100 text-blue-600';
      case 'job':
        return 'bg-emerald-100 text-emerald-600';
      case 'payment':
        return 'bg-amber-100 text-amber-600';
      case 'jobseeker':
        return 'bg-violet-100 text-violet-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Dashboard</h1>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <div className="card border-l-4 border-primary-600 bg-gradient-to-br from-blue-50 to-indigo-100 hover:shadow-lg transition-shadow">
          <h2 className="text-gray-700 text-sm font-medium mb-2">Total Users</h2>
          <div className="flex justify-between items-end">
            <p className="text-3xl font-bold text-indigo-900">
              {isLoading ? '...' : dashboardData.totalUsers}
            </p>
            <span className="text-sm flex items-center">
              {performanceMetrics.userGrowth > 0 ? (
                <>
                  <svg className="w-4 h-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-green-600">{performanceMetrics.userGrowth}%</span>
                </>
              ) : performanceMetrics.userGrowth < 0 ? (
                <>
                  <svg className="w-4 h-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M12 13a1 1 0 110 2H7a1 1 0 01-1-1v-5a1 1 0 112 0v2.586l4.293-4.293a1 1 0 011.414 0L16 9.586 20.293 5.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0L12 8.414l-3.293 3.293a1 1 0 01-1.414 0L7 11.586V13H12z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-red-600">{Math.abs(performanceMetrics.userGrowth)}%</span>
                </>
              ) : (
                <span className="text-gray-500">0%</span>
              )}
            </span>
          </div>
        </div>
        
        <div className="card border-l-4 border-emerald-500 bg-gradient-to-br from-green-50 to-emerald-100 hover:shadow-lg transition-shadow">
          <h2 className="text-gray-700 text-sm font-medium mb-2">Active Jobs</h2>
          <div className="flex justify-between items-end">
            <p className="text-3xl font-bold text-emerald-900">
              {isLoading ? '...' : dashboardData.activeJobs}
            </p>
            <span className="text-sm flex items-center">
              {performanceMetrics.jobPostings > 0 ? (
                <>
                  <svg className="w-4 h-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-green-600">{performanceMetrics.jobPostings}%</span>
                </>
              ) : performanceMetrics.jobPostings < 0 ? (
                <>
                  <svg className="w-4 h-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M12 13a1 1 0 110 2H7a1 1 0 01-1-1v-5a1 1 0 112 0v2.586l4.293-4.293a1 1 0 011.414 0L16 9.586 20.293 5.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0L12 8.414l-3.293 3.293a1 1 0 01-1.414 0L7 11.586V13H12z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-red-600">{Math.abs(performanceMetrics.jobPostings)}%</span>
                </>
              ) : (
                <span className="text-gray-500">0%</span>
              )}
            </span>
          </div>
        </div>
        
        <div className="card border-l-4 border-violet-500 bg-gradient-to-br from-purple-50 to-violet-100 hover:shadow-lg transition-shadow">
          <h2 className="text-gray-700 text-sm font-medium mb-2">Premium Members</h2>
          <div className="flex justify-between items-end">
            <p className="text-3xl font-bold text-violet-900">
              {isLoading ? '...' : dashboardData.premiumMembers}
            </p>
            <span className="text-sm flex items-center">
              {performanceMetrics.subscriptions > 0 ? (
                <>
                  <svg className="w-4 h-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-green-600">{performanceMetrics.subscriptions}%</span>
                </>
              ) : performanceMetrics.subscriptions < 0 ? (
                <>
                  <svg className="w-4 h-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M12 13a1 1 0 110 2H7a1 1 0 01-1-1v-5a1 1 0 112 0v2.586l4.293-4.293a1 1 0 011.414 0L16 9.586 20.293 5.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0L12 8.414l-3.293 3.293a1 1 0 01-1.414 0L7 11.586V13H12z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-red-600">{Math.abs(performanceMetrics.subscriptions)}%</span>
                </>
              ) : (
                <span className="text-gray-500">0%</span>
              )}
            </span>
          </div>
        </div>
        
        <div className="card border-l-4 border-amber-500 bg-gradient-to-br from-yellow-50 to-amber-100 hover:shadow-lg transition-shadow">
          <h2 className="text-gray-700 text-sm font-medium mb-2">Revenue</h2>
          <div className="flex justify-between items-end">
            <p className="text-3xl font-bold text-amber-900">
              {isLoading ? '...' : `₹${dashboardData.revenue.toFixed(1)}`}
            </p>
            <span className="text-sm flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"></path>
              </svg>
              <span className="text-green-600">12%</span>
            </span>
          </div>
        </div>
      </div>
      
      {/* Display error if any */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <p>{error}</p>
        </div>
      )}
      
      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6">
        {/* Recent Activity Section - Now full width with improved listing */}
        <div className="card bg-white hover:shadow-md transition-shadow border border-gray-200 rounded-xl">
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-lg font-semibold text-gray-800">Recent Activity</h2>
          </div>
          
          <div className="space-y-4">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
              </div>
            ) : recentActivity.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="py-3 flex items-start">
                    <div className={`p-2 rounded-full mr-4 ${getActivityIconBackground(activity.type)}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <h3 className="font-medium text-gray-800">{activity.title}</h3>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          {getRelativeTime(activity.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{activity.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent activity found</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Recent Jobs Section */}
      <div className="card mt-6 bg-white hover:shadow-md transition-shadow border border-gray-200 rounded-xl">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-800">Recent Job Listings</h2>
          <Link to="/jobs/active" className="btn bg-primary-600 text-white hover:bg-primary-600/90 text-sm rounded-full">View All Jobs</Link>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b bg-gray-50">
                <th className="px-4 py-3">Job Title</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Posted</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-4 text-center">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                  </td>
                </tr>
              ) : recentJobs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-4 text-center text-gray-500">
                    No job listings found.
                  </td>
                </tr>
              ) : (
                recentJobs.map((job) => (
                  <tr key={job.id} className="text-sm hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{job.title}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{job.salon_name}</td>
                    <td className="px-4 py-3 text-gray-600">{job.location}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(getJobDate(job))}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(getJobStatus(job))}`}>
                        {getJobStatus(job)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
